/**
 * Touch policy that keeps navigating the canvas from editing it.
 *
 * On coarse-pointer devices a one-finger press on something that isn't
 * selected is a pan unless the finger holds still long enough to pick it up.
 * The selected item follows the finger straight away (past a small travel
 * threshold so a tap never nudges it). Fine pointers keep immediate drag.
 */

/** Hold time before a press on an unselected item picks it up, ms. */
export const HOLD_MS = 300;
/** Finger travel that turns a press into a pan and cancels a pickup, px.
 *  Also the Stage's drag distance on touch so a pan and a pickup can't
 *  both start from the same press. */
export const SLOP_PX = 8;
/** Travel before a selected item starts moving under a finger, px. */
export const TOUCH_DRAG_DISTANCE = 6;

export type PressOutcome = "pending" | "pan" | "pickup" | "tap";

export interface PressState {
  elapsedMs: number;
  /** Straight-line finger travel since the press, px. */
  movedPx: number;
  released: boolean;
  /** Fingers currently down. */
  fingers: number;
}

/** What a press on an unselected item has turned into so far. */
export function classifyPress(s: PressState): PressOutcome {
  if (s.fingers > 1 || s.movedPx > SLOP_PX) return "pan";
  if (s.elapsedMs >= HOLD_MS) return "pickup";
  return s.released ? "tap" : "pending";
}

/** Whether an item follows the pointer as soon as it moves. */
export function dragsImmediately(opts: { coarse: boolean; selected: boolean }): boolean {
  return !opts.coarse || opts.selected;
}

const HOLD_HINT_KEY = "room-canvas:hint:hold-to-move";
/** Times the "Hold a piece to move it" hint is shown, ever. */
export const HOLD_HINT_MAX = 2;

const ADJUST_HINT_KEY = "room-canvas:hint:adjust";
/** Times the "Drag to move · Adjust to resize or rotate" tip is shown, ever. */
export const ADJUST_HINT_MAX = 1;

const defaultStorage = (): Storage | null => (typeof localStorage === "undefined" ? null : localStorage);

function takeLatchedHint(key: string, max: number, storage: Storage | null): boolean {
  if (!storage) return false;
  try {
    const shown = Number(storage.getItem(key) ?? 0) || 0;
    if (shown >= max) return false;
    storage.setItem(key, String(shown + 1));
    return true;
  } catch {
    return false;
  }
}

/**
 * Claim one showing of the hold-to-move hint. Returns false once it has been
 * shown {@link HOLD_HINT_MAX} times (or storage is unavailable).
 */
export function takeHoldHint(storage: Storage | null = defaultStorage()): boolean {
  return takeLatchedHint(HOLD_HINT_KEY, HOLD_HINT_MAX, storage);
}

/** Claim the one showing of the Adjust coach tip on a first phone selection. */
export function takeAdjustHint(storage: Storage | null = defaultStorage()): boolean {
  return takeLatchedHint(ADJUST_HINT_KEY, ADJUST_HINT_MAX, storage);
}
